/**
 * Mirror of `core-api/src/constants/permissions.ts`. Keys must stay in sync.
 *
 * The backend is authoritative — these constants are just the catalogue used
 * to gate UI elements (sidebar, route guards, action buttons). The set of
 * permissions a user actually has comes from `GET /v1/user`.
 */
export const PERMISSIONS = {
  PAGE_DASHBOARD: "page.dashboard",
  PAGE_USERS: "page.users",
  PAGE_USER_PROFILE: "page.user_profile",
  PAGE_KYC: "page.kyc",
  PAGE_TRANSACTIONS: "page.transactions",
  PAGE_BANK_TRANSFERS: "page.bank_transfers",
  PAGE_BONUSES: "page.bonuses",
  PAGE_BONUS2: "page.bonus2",
  PAGE_TOKEN: "page.token",
  // Mass minting is a materially different privilege from day-to-day token
  // administration, so it is NOT implied by PAGE_TOKEN.
  PAGE_TOKEN_BULK_GRANT: "page.token.bulk_grant",
  PAGE_PROMO_CODES: "page.promo_codes",
  PAGE_REFERRALS: "page.referrals",
  PAGE_TAGS: "page.tags",
  PAGE_CMS: "page.cms",
  PAGE_CMS_ADMIN_TABS: "page.cms.admin_tabs",
  PAGE_GAME_MANAGEMENT: "page.game_management",
  PAGE_CHALLENGES: "page.challenges",
  PAGE_TOURNAMENTS: "page.tournaments",
  PAGE_BETBY_BONUSES: "page.betby_bonuses",
  PAGE_ACTIVITY: "page.activity",
  PAGE_RISK: "page.risk",
  PAGE_PROMOTIONS: "page.promotions",
  PAGE_SETTINGS: "page.settings",
  PAGE_ADMINS: "page.admins",
  PAGE_BALANCE: "page.balance",
  PAGE_GGR_REPORT: "page.ggr_report",
  PAGE_ROLE_MANAGEMENT: "page.role_management",
  PAGE_POSTBACKS: "page.postbacks",

  USER_EDIT_ROLE: "user.edit_role",
  USER_EDIT_BALANCE: "user.edit_balance",
  TOKEN_BULK_GRANT_EXECUTE: "token.bulk_grant.execute",
  // The token write surface, split off from PAGE_TOKEN so that reading the
  // presale figures does not carry the right to change the price, move pool
  // money, or mint into a player's balance.
  TOKEN_CONFIG_EDIT: "token.config.edit",
  TOKEN_POOL_MANAGE: "token.pool.manage",
  TOKEN_MANUAL_OPS: "token.manual_ops",
  USER_EDIT_RAKEBACK: "user.edit_rakeback",
  WITHDRAWAL_APPROVE: "withdrawal.approve",
  BONUS_CREATE: "bonus.create",
  BONUS_LOW_WAGER: "bonus.low_wager_min",
  BONUS2_PLAYER_MANAGE: "bonus2.player_manage",
  PROMO_CODE_CREATE: "promo_code.create",
  SETTINGS_CUSTOMERIO: "settings.customerio",
  SETTINGS_LIVE_CHAT: "settings.live_chat",
  SETTINGS_MAINTENANCE: "settings.maintenance",
  SETTINGS_FAKE_BETS: "settings.fake_bets",
  SETTINGS_RESET: "settings.reset",
  GAME_UPDATE_BYPASS_COOLDOWN: "game.update.bypass_cooldown",
  PROVIDER_FEES_EDIT: "provider.fees_edit",
  REPORT_GGR_EXPORT: "report.ggr.export",
  GGR_INSIGHT_VIEW: "ggr.insight_view",
  ROLE_MANAGEMENT_EDIT: "role_management.edit"
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS) as Permission[]

/**
 * Human-readable labels for the Role Management UI. Group is used for section
 * headers in the matrix. Not localized — these are admin-only strings.
 */
export const PERMISSION_META: Record<Permission, { label: string; group: "Pages" | "Actions" }> = {
  [PERMISSIONS.PAGE_DASHBOARD]: { label: "Dashboard", group: "Pages" },
  [PERMISSIONS.PAGE_USERS]: { label: "Users — list", group: "Pages" },
  [PERMISSIONS.PAGE_USER_PROFILE]: { label: "Users — profile", group: "Pages" },
  [PERMISSIONS.PAGE_KYC]: { label: "KYC files", group: "Pages" },
  [PERMISSIONS.PAGE_TRANSACTIONS]: { label: "Transactions", group: "Pages" },
  [PERMISSIONS.PAGE_BANK_TRANSFERS]: { label: "Bank transfers / withdrawals", group: "Pages" },
  [PERMISSIONS.PAGE_BONUSES]: { label: "Bonuses", group: "Pages" },
  [PERMISSIONS.PAGE_BONUS2]: { label: "Bonuses V2", group: "Pages" },
  [PERMISSIONS.PAGE_TOKEN]: { label: "Token", group: "Pages" },
  [PERMISSIONS.PAGE_TOKEN_BULK_GRANT]: { label: "Token bulk grant", group: "Pages" },
  [PERMISSIONS.PAGE_PROMO_CODES]: { label: "Promo codes", group: "Pages" },
  [PERMISSIONS.PAGE_REFERRALS]: { label: "Referrals / affiliates", group: "Pages" },
  [PERMISSIONS.PAGE_TAGS]: { label: "Segmentation / tags", group: "Pages" },
  [PERMISSIONS.PAGE_CMS]: { label: "CMS", group: "Pages" },
  [PERMISSIONS.PAGE_CMS_ADMIN_TABS]: { label: "CMS — admin tabs", group: "Pages" },
  [PERMISSIONS.PAGE_GAME_MANAGEMENT]: { label: "Game management", group: "Pages" },
  [PERMISSIONS.PAGE_CHALLENGES]: { label: "Challenges", group: "Pages" },
  [PERMISSIONS.PAGE_TOURNAMENTS]: { label: "Tournaments", group: "Pages" },
  [PERMISSIONS.PAGE_BETBY_BONUSES]: { label: "Betby bonuses", group: "Pages" },
  [PERMISSIONS.PAGE_ACTIVITY]: { label: "Activity log", group: "Pages" },
  [PERMISSIONS.PAGE_RISK]: { label: "Risk & fraud", group: "Pages" },
  [PERMISSIONS.PAGE_PROMOTIONS]: { label: "Promotions", group: "Pages" },
  [PERMISSIONS.PAGE_SETTINGS]: { label: "Settings", group: "Pages" },
  [PERMISSIONS.PAGE_ADMINS]: { label: "Admin users", group: "Pages" },
  [PERMISSIONS.PAGE_BALANCE]: { label: "PSP balances", group: "Pages" },
  [PERMISSIONS.PAGE_GGR_REPORT]: { label: "GGR monthly report", group: "Pages" },
  [PERMISSIONS.PAGE_ROLE_MANAGEMENT]: { label: "Role management", group: "Pages" },
  [PERMISSIONS.PAGE_POSTBACKS]: { label: "S2S postbacks", group: "Pages" },

  [PERMISSIONS.USER_EDIT_ROLE]: { label: "Edit user role", group: "Actions" },
  [PERMISSIONS.USER_EDIT_BALANCE]: { label: "Edit user balance", group: "Actions" },
  [PERMISSIONS.TOKEN_BULK_GRANT_EXECUTE]: { label: "Run token bulk grant", group: "Actions" },
  [PERMISSIONS.TOKEN_CONFIG_EDIT]: { label: "Token — edit presale config", group: "Actions" },
  [PERMISSIONS.TOKEN_POOL_MANAGE]: { label: "Token — manage pools & distributions", group: "Actions" },
  [PERMISSIONS.TOKEN_MANUAL_OPS]: { label: "Token — manual money operations", group: "Actions" },
  [PERMISSIONS.USER_EDIT_RAKEBACK]: { label: "Edit user rakeback", group: "Actions" },
  [PERMISSIONS.WITHDRAWAL_APPROVE]: { label: "Approve / decline withdrawals", group: "Actions" },
  [PERMISSIONS.BONUS_CREATE]: { label: "Create bonus", group: "Actions" },
  [PERMISSIONS.BONUS_LOW_WAGER]: { label: "Override custom-bonus min wager", group: "Actions" },
  [PERMISSIONS.BONUS2_PLAYER_MANAGE]: { label: "Manage a player's bonuses", group: "Actions" },
  [PERMISSIONS.PROMO_CODE_CREATE]: { label: "Create promo code", group: "Actions" },
  [PERMISSIONS.SETTINGS_CUSTOMERIO]: { label: "Settings — Customer.io", group: "Actions" },
  [PERMISSIONS.SETTINGS_LIVE_CHAT]: { label: "Settings — Live Chat", group: "Actions" },
  [PERMISSIONS.SETTINGS_MAINTENANCE]: { label: "Settings — Maintenance", group: "Actions" },
  [PERMISSIONS.SETTINGS_FAKE_BETS]: { label: "Settings — Fake Bets", group: "Actions" },
  [PERMISSIONS.SETTINGS_RESET]: { label: "Settings — Reset to defaults", group: "Actions" },
  [PERMISSIONS.GAME_UPDATE_BYPASS_COOLDOWN]: { label: "Bypass games-update cooldown", group: "Actions" },
  [PERMISSIONS.PROVIDER_FEES_EDIT]: { label: "Edit provider fees & restrictions", group: "Actions" },
  [PERMISSIONS.REPORT_GGR_EXPORT]: { label: "Export GGR reports", group: "Actions" },
  [PERMISSIONS.GGR_INSIGHT_VIEW]: { label: "View GGR / NGR breakdown", group: "Actions" },
  [PERMISSIONS.ROLE_MANAGEMENT_EDIT]: { label: "Edit role management", group: "Actions" }
}
